import * as THREE from "three";
import {
  TERRAIN_PRESETS,
  applyTerrainCommand,
  classifyTraversal,
  cloneTerrainState,
  createTerrainState,
  formatFeatureSummary,
  generatePois,
  generateTerrain,
  sampleTerrain,
  terrainSlope,
} from "./terrainEngine.js";
import { terrainFreeCameraSpeed } from "./terrainNavigation.js";

const POI_COLORS = {
  settlement: 0xf0a64a,
  lookout: 0xe0d7b6,
  lakeside: 0x58bdc4,
};

function makePoiLabel(text, color) {
  const canvas = document.createElement("canvas");
  canvas.width = 384;
  canvas.height = 96;
  const context = canvas.getContext("2d");
  context.fillStyle = "rgba(9, 13, 12, .88)";
  context.roundRect(4, 4, 376, 82, 12);
  context.fill();
  context.strokeStyle = `#${color.toString(16).padStart(6, "0")}`;
  context.lineWidth = 4;
  context.stroke();
  context.fillStyle = "#f3efe4";
  context.font = "600 30px Georgia, serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(text.slice(0, 26), 192, 46);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false }));
  sprite.scale.set(18, 4.5, 1);
  sprite.position.y = 11;
  return sprite;
}

function featureIcon(type) {
  return ({ mountain: "▲", ridge: "⌁", valley: "∨", lake: "◉", river: "≋", plateau: "▬", pass: "↘" })[type] ?? "◆";
}

function rangeField({ label, key, value, min, max, step = 1, unit = "" }) {
  return `<label class="terrain-live-range"><span>${label}<b data-range-output="${key}">${Number(value).toFixed(step < 1 ? 2 : 0)}${unit}</b></span><input type="range" min="${min}" max="${max}" step="${step}" value="${value}" data-terrain-range="${key}"></label>`;
}

const TERRAIN_RECIPE_SCHEMA = "terrain_recipe.v1";

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

function finite(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function pointToCanonical(point) {
  return { id: String(point.id), point: { xM: finite(point.x), yM: finite(point.z) } };
}

function pointFromCanonical(point, fallbackId) {
  const position = point?.point ?? point;
  return {
    id: String(point?.id || fallbackId),
    x: finite(position?.xM, finite(position?.x)),
    z: finite(position?.yM, finite(position?.z)),
    ...(point?.manual ? { manual: true } : {}),
  };
}

const stableId = (value, fallback) => String(value || fallback).toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/^[^a-z]/, "t_").slice(0, 64);
const unit = (value, min, max, fallback) => Math.min(Math.max(finite(value, fallback), min), max);

function canonicalPolygon(points, id) {
  return { id: stableId(id, "boundary"), points: (points ?? []).map(pointToCanonical) };
}

/** Keep the server revision payload separate from the fast, local preview state.
 * This makes the editor compatible with schema evolution without letting a UI
 * implementation detail become the production contract. */
export function serializeTerrainRecipe(state, { mapId } = {}) {
  const snapshot = copy(state);
  const boundary = snapshot.boundary ?? {};
  const outer = Array.isArray(boundary.outer) ? boundary.outer : [];
  const holes = Array.isArray(boundary.holes) ? boundary.holes : [];
  const poiRules = snapshot.poiRules ?? [];
  const elevationMin = finite(snapshot.elevationMin, finite(snapshot.baseHeight, 12) - 220);
  const elevationMax = Math.max(finite(snapshot.elevationMax, finite(snapshot.baseHeight, 12) + 320), elevationMin + 1);
  const traversal = snapshot.traversal ?? {};
  const normalizedRegions = (snapshot.constraints?.regions ?? []).flatMap((region, index) => {
    const points = region?.polygon?.points ?? region?.polygon ?? region?.boundary?.outer ?? region?.points;
    if (!Array.isArray(points) || points.length < 3) return [];
    return [{
      id: stableId(region.id, `region_${index + 1}`),
      mode: ["walkable", "climbable", "blocked"].includes(region.mode) ? region.mode : "blocked",
      polygon: canonicalPolygon(points, region.polygon?.id ?? `region_${index + 1}_polygon`),
      ...(Number.isFinite(region.maxSlopeDeg) ? { maxSlopeDeg: unit(region.maxSlopeDeg, 0.1, 89, 45) } : {}),
    }];
  });
  return {
    schema_version: TERRAIN_RECIPE_SCHEMA,
    terrain_uid: snapshot.terrainUid,
    boundary: {
      outer: canonicalPolygon(outer, "outer_boundary"),
      holes: holes.filter((ring) => Array.isArray(ring) && ring.length >= 3)
        .map((ring, index) => canonicalPolygon(ring, `hole_${index + 1}`)),
    },
    generation: {
      elevation: { minM: elevationMin, maxM: elevationMax, baseM: unit(snapshot.baseHeight, elevationMin, elevationMax, 12) },
      roughness: unit(snapshot.roughness, 0, 1, 0.6),
      macroFeatureScaleM: unit(snapshot.macroFeatureScaleM, 1, 100000, 310),
      detailFeatureScaleM: unit(snapshot.detailFeatureScaleM, 1, unit(snapshot.macroFeatureScaleM, 1, 100000, 310), 92),
      erosionIterations: Math.round(unit(snapshot.erosion, 0, 10000, 0)),
      waterLevelM: unit(snapshot.waterLevel, elevationMin, elevationMax, 0),
      seeds: {
        terrain: Math.round(unit(snapshot.seed, 0, 2147483647, 1)),
        hydrology: Math.round(unit(snapshot.hydrologySeed, 0, 2147483647, snapshot.seed)),
        poi: Math.round(unit(snapshot.poiSeed, 0, 2147483647, snapshot.seed)),
        dressing: Math.round(unit(snapshot.dressingSeed, 0, 2147483647, snapshot.seed)),
      },
    },
    traversal: {
      walkableMaxSlopeDeg: unit(traversal.walkableMaxSlopeDeg, 0.1, 89, 32),
      climbableMaxSlopeDeg: Math.max(unit(traversal.climbableMaxSlopeDeg, 0.1, 89, 52), unit(traversal.walkableMaxSlopeDeg, 0.1, 89, 32) + 0.1),
      regions: normalizedRegions,
    },
    landforms: (snapshot.features ?? []).map((feature) => ({
      id: stableId(feature.id, "landform"), type: feature.type,
      point: { xM: finite(feature.x), yM: finite(feature.z) },
      ...(Number.isFinite(feature.angle) ? { angleDeg: feature.angle * 180 / Math.PI } : {}),
      ...(Number.isFinite(feature.radius) ? { radiusM: feature.radius } : {}),
      ...(Number.isFinite(feature.length) ? { lengthM: feature.length } : {}),
      ...(Number.isFinite(feature.height) ? { heightM: feature.height } : {}),
      ...(Number.isFinite(feature.depth) ? { depthM: feature.depth } : {}),
      ...(Number.isFinite(feature.falloff) ? { falloffM: feature.falloff } : {}),
    })),
    poi: {
      rules: (poiRules.length ? poiRules : [{ id: "poi", label: "Point of interest", type: "poi" }]).map((rule, index) => {
        const radiusM = Math.max(1, finite(rule.radius, 24));
        return {
          id: stableId(rule.id, `poi_${index + 1}`), label: String(rule.label || rule.type || "Point of interest").slice(0, 120), kind: stableId(rule.type, "poi"),
          count: Math.max(0, Math.round(finite(rule.count, 0))), radiusM,
          padRadiusM: Math.max(radiusM, finite(rule.padRadius, radiusM)), padBlendM: Math.max(0, finite(rule.blend, 10)),
          minSpacingM: Math.max(0, finite(rule.minSpacing, 80)), maxSourceSlopeDeg: unit(rule.maxSlope, 0.1, 89, 18),
        };
      }),
      overrides: (snapshot.pois ?? []).filter((poi) => poi.manual || poi.pinned).map((poi) => ({
        id: stableId(poi.id, "poi"), point: { xM: finite(poi.x), yM: finite(poi.z) }, pinned: true, manual: true,
        ...(poi.ruleId ? { ruleId: stableId(poi.ruleId, "poi") } : {}), ...(poi.label ? { label: String(poi.label).slice(0, 120) } : {}),
        ...(Number.isFinite(poi.radius) ? { radiusM: Math.max(1, poi.radius) } : {}),
        ...(Number.isFinite(poi.padRadius) ? { padRadiusM: Math.max(Number(poi.radius) || 1, poi.padRadius) } : {}),
        ...(Number.isFinite(poi.minSpacing) ? { minSpacingM: Math.max(0, poi.minSpacing) } : {}),
        ...(Number.isFinite(poi.maxSlope) ? { maxSourceSlopeDeg: unit(poi.maxSlope, 0.1, 89, 18) } : {}),
      })),
    },
    manualHeightControls: (snapshot.manualHeightControls ?? []).map((control, index) => ({
      id: stableId(control.id, `height_${index + 1}`), point: { xM: finite(control.x), yM: finite(control.z) },
      targetElevationM: unit(control.height, elevationMin, elevationMax, finite(snapshot.baseHeight, 12)),
      radiusM: Math.max(1, finite(control.radius, 30)), blendM: Math.max(0, finite(control.blend, 10)),
    })),
    unreal: {
      landscapeResolution: Math.round(unit(snapshot.unreal?.landscapeResolution, 7, 16381, 2017)),
      xyScaleM: unit(snapshot.unreal?.xyScaleM, 0.001, 100, 1),
      editLayerStack: ["WL_Base", "WL_Hydrology", "WL_Traversal", "WL_POI", "WL_Manual", "WL_Visibility"],
    },
  };
}

export function deserializeTerrainRecipe(recipe, fallbackState) {
  if (!recipe || recipe.schema_version !== TERRAIN_RECIPE_SCHEMA) return null;
  const state = copy(fallbackState);
  const parameters = recipe.generation ?? recipe.parameters ?? {};
  const boundary = recipe.boundary ?? {};
  const outer = boundary.outer?.points ?? boundary.outer;
  const holes = boundary.holes ?? [];
  if (Array.isArray(outer) && outer.length >= 3) {
    state.boundary = {
      outer: outer.map((point, index) => pointFromCanonical(point, `outer_${index + 1}`)),
      holes: Array.isArray(holes)
        ? holes.map((ring) => ring?.points ?? ring).filter((ring) => Array.isArray(ring) && ring.length >= 3)
          .map((ring, ringIndex) => ring.map((point, index) => pointFromCanonical(point, `hole_${ringIndex + 1}_${index + 1}`)))
        : [],
    };
  }
  state.terrainUid = recipe.terrain_uid ?? state.terrainUid;
  state.baseHeight = finite(parameters.elevation?.baseM, state.baseHeight);
  state.seed = finite(parameters.seeds?.terrain, parameters.seed ?? state.seed);
  state.hydrologySeed = finite(parameters.seeds?.hydrology, state.hydrologySeed);
  state.poiSeed = finite(parameters.seeds?.poi, parameters.poiSeed ?? state.poiSeed);
  state.dressingSeed = finite(parameters.seeds?.dressing, state.dressingSeed);
  state.roughness = finite(parameters.roughness, state.roughness);
  state.erosion = finite(parameters.erosionIterations, parameters.erosion ?? state.erosion);
  state.waterLevel = finite(parameters.waterLevelM, state.waterLevel);
  const traversal = copy(recipe.traversal ?? parameters.traversal ?? state.traversal ?? {});
  const regions = (recipe.traversal?.regions ?? parameters.constraints?.regions ?? []).map((region, index) => ({
    id: region.id ?? `region_${index + 1}`,
    mode: region.mode,
    ...(Number.isFinite(region.maxSlopeDeg) ? { maxSlopeDeg: region.maxSlopeDeg } : {}),
    polygon: (region.polygon?.points ?? region.polygon ?? []).map((point, pointIndex) => pointFromCanonical(point, `region_${index + 1}_${pointIndex + 1}`)),
  }));
  state.traversal = { ...traversal, regions };
  state.constraints = { ...(state.constraints ?? {}), regions };
  if (Array.isArray(recipe.landforms)) state.features = recipe.landforms.map((feature) => ({
    id: feature.id, type: feature.type, x: feature.point.xM, z: feature.point.yM,
    ...(Number.isFinite(feature.angleDeg) ? { angle: feature.angleDeg * Math.PI / 180 } : {}),
    ...(Number.isFinite(feature.radiusM) ? { radius: feature.radiusM } : {}), ...(Number.isFinite(feature.lengthM) ? { length: feature.lengthM } : {}),
    ...(Number.isFinite(feature.heightM) ? { height: feature.heightM } : {}), ...(Number.isFinite(feature.depthM) ? { depth: feature.depthM } : {}), ...(Number.isFinite(feature.falloffM) ? { falloff: feature.falloffM } : {}),
  }));
  if (Array.isArray(recipe.poi?.rules)) state.poiRules = recipe.poi.rules.map((rule) => ({ id: rule.id, type: rule.kind, label: rule.label, count: rule.count, radius: rule.radiusM, padRadius: rule.padRadiusM, blend: rule.padBlendM, minSpacing: rule.minSpacingM, maxSlope: rule.maxSourceSlopeDeg }));
  if (Array.isArray(recipe.poi?.overrides)) state.pois = recipe.poi.overrides.map((poi) => ({
    id: poi.id,
    label: poi.label ?? poi.id.replaceAll("_", " "),
    type: recipe.poi.rules.find((rule) => rule.id === poi.ruleId)?.kind ?? "settlement",
    ruleId: poi.ruleId,
    x: poi.point.xM,
    z: poi.point.yM,
    manual: poi.manual,
    pinned: poi.pinned,
    ...(Number.isFinite(poi.radiusM) ? { radius: poi.radiusM } : {}),
    ...(Number.isFinite(poi.padRadiusM) ? { padRadius: poi.padRadiusM } : {}),
    ...(Number.isFinite(poi.minSpacingM) ? { minSpacing: poi.minSpacingM } : {}),
    ...(Number.isFinite(poi.maxSourceSlopeDeg) ? { maxSlope: poi.maxSourceSlopeDeg } : {}),
  }));
  if (Array.isArray(recipe.manualHeightControls)) state.manualHeightControls = recipe.manualHeightControls.map((control) => ({ id: control.id, x: control.point.xM, z: control.point.yM, height: control.targetElevationM, radius: control.radiusM, blend: control.blendM }));
  state.unreal = copy(recipe.unreal ?? state.unreal ?? {});
  return state;
}

export class TerrainEditor {
  constructor({ viewer, viewport, sidebar, detail, toast, esc, transcribe, getWorldId, getWorldData, getProjectId, api, onExit }) {
    this.viewer = viewer;
    this.viewport = viewport;
    this.sidebar = sidebar;
    this.detail = detail;
    this.toast = toast;
    this.esc = esc;
    this.transcribe = transcribe;
    this.getWorldId = getWorldId;
    this.getWorldData = getWorldData;
    this.getProjectId = getProjectId ?? (() => "");
    this.api = api;
    this.onExit = onExit;
    this.active = false;
    this.freeCamera = false;
    this.state = null;
    this.terrain = null;
    this.group = null;
    this.terrainMesh = null;
    this.poiGroup = null;
    this.boundaryGroup = null;
    this.heightControlGroup = null;
    this.reticle = null;
    this.anchor = { x: 0, z: 0 };
    this.history = [];
    this.draggingPoiId = null;
    this.draggingBoundaryPoint = null;
    this.selectedPoiId = null;
    this.selectedHeightControlId = null;
    this.recorder = null;
    this.recordChunks = [];
    this.recordAnchor = null;
    this.raycaster = new THREE.Raycaster();
    this.persistence = { artifactId: null, baseRevisionId: null, revisionNumber: null, status: "local", loading: false, saving: false };
    this._regenerateFrame = null;
    this._remoteLoadToken = 0;
    this._boundPointerMove = (event) => this.onPointerMove(event);
    this._boundPointerDown = (event) => this.onPointerDown(event);
    this._boundPointerUp = (event) => this.onPointerUp(event);
    this._boundPointerCancel = (event) => this.onPointerCancel(event);
    this._boundKeyDown = (event) => this.onKeyDown(event);
  }

  get storageKey() {
    return `worldloom:terrain-editor:${this.getWorldId() || "draft"}`;
  }

  loadState(size) {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey));
      if (saved?.version === 1 && Array.isArray(saved.features) && Array.isArray(saved.poiRules)) return saved;
      if (saved?.version >= 2 && Array.isArray(saved.features) && Array.isArray(saved.poiRules)) return saved;
    } catch {}
    return createTerrainState({ size });
  }

  ensureAuthoringState() {
    if (!this.state.boundary?.outer?.length) {
      const radius = this.state.size * 0.46;
      const holeRadius = this.state.size * 0.17;
      const ring = (prefix, radiusValue, offset = 0) => Array.from({ length: 10 }, (_, index) => {
        const angle = offset + (index / 10) * Math.PI * 2;
        return { id: `${prefix}_${index + 1}`, x: Math.cos(angle) * radiusValue, z: Math.sin(angle) * radiusValue };
      });
      this.state.boundary = { outer: ring("outer", radius, Math.PI / 10), holes: [ring("hole_1", holeRadius)] };
    }
    this.state.traversal ??= {};
    this.state.constraints ??= {};
    this.state.manualHeightControls ??= [];
    this.state.terrainUid ??= (globalThis.crypto?.randomUUID?.() ?? "00000000-0000-4000-8000-000000000001");
  }

  saveState() {
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch {}
  }

  currentMapId() {
    return this.getWorldId?.() || "";
  }

  currentProjectId() {
    return this.getProjectId?.() || "";
  }

  async loadRemoteRecipe() {
    const projectId = this.currentProjectId();
    const mapId = this.currentMapId();
    if (!this.api || !projectId || !mapId) return;
    const token = ++this._remoteLoadToken;
    this.persistence.loading = true;
    this.renderPersistenceStatus();
    try {
      const query = new URLSearchParams({ kind: "terrain_recipe", mapId });
      const listed = await this.api(`/projects/${encodeURIComponent(projectId)}/artifacts?${query}`);
      const artifact = listed?.artifacts?.[0];
      if (!artifact || token !== this._remoteLoadToken || !this.active) return;
      const detail = await this.api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(artifact.id)}`);
      if (token !== this._remoteLoadToken || !this.active) return;
      const headId = artifact.workingRevisionId ?? artifact.working_revision_id ?? artifact.approvedRevisionId ?? artifact.approved_revision_id ?? null;
      const revision = detail?.revisions?.find((candidate) => candidate.id === headId) ?? detail?.revisions?.[0] ?? null;
      const restored = deserializeTerrainRecipe(revision?.body, this.state);
      this.persistence = {
        artifactId: artifact.id,
        baseRevisionId: revision?.id ?? null,
        revisionNumber: revision?.revisionNumber ?? revision?.revision_number ?? null,
        status: revision?.status ?? "local",
        loading: false,
        saving: false,
      };
      if (restored) {
        this.state = restored;
        this.ensureAuthoringState();
        this.selectedPoiId = null;
        this.regenerate({ pois: true });
        this.setStatus(`Loaded terrain recipe revision ${this.persistence.revisionNumber ?? ""}.`.trim());
      }
    } catch (error) {
      if (token === this._remoteLoadToken) {
        this.persistence.loading = false;
        this.persistence.status = "local";
        this.setStatus(`Using local preview; recipe history could not be loaded: ${String(error)}`, true);
      }
    } finally {
      if (token === this._remoteLoadToken) {
        this.persistence.loading = false;
        this.renderPersistenceStatus();
      }
    }
  }

  async saveCandidate() {
    const projectId = this.currentProjectId();
    const mapId = this.currentMapId();
    if (!this.api || !projectId || !mapId) return this.setStatus("Select a project and world before saving a terrain recipe.", true);
    if (this.persistence.saving) return;
    this.persistence.saving = true;
    this.renderPersistenceStatus();
    try {
      let artifactId = this.persistence.artifactId;
      if (!artifactId) {
        const created = await this.api(`/projects/${encodeURIComponent(projectId)}/artifacts`, {
          method: "POST",
          body: JSON.stringify({ kind: "terrain_recipe", name: "Terrain recipe", mapId }),
        });
        artifactId = created?.artifact?.id;
      }
      if (!artifactId) throw new Error("terrain recipe artifact was not returned");

      // Read the head immediately before authoring.  The generic artifact API
      // creates immutable children; this preflight avoids silently branching a
      // designer's stale local preview beneath a newer shared revision.
      const latest = await this.api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(artifactId)}`);
      const artifact = latest?.artifact ?? {};
      const headId = artifact.workingRevisionId ?? artifact.working_revision_id ?? artifact.approvedRevisionId ?? artifact.approved_revision_id ?? null;
      if (this.persistence.baseRevisionId && headId && headId !== this.persistence.baseRevisionId) {
        this.persistence.artifactId = artifactId;
        this.persistence.baseRevisionId = headId;
        this.persistence.status = "stale";
        this.renderPersistenceStatus();
        this.setStatus("Terrain recipe changed elsewhere. Reloaded head is required before saving your local edits.", true);
        return;
      }
      const body = serializeTerrainRecipe(this.state, { mapId });
      const created = await this.api(`/projects/${encodeURIComponent(projectId)}/artifacts/${encodeURIComponent(artifactId)}/revisions`, {
        method: "POST",
        body: JSON.stringify({
          parentRevisionId: headId,
          expectedHeadRevisionId: headId,
          body,
          changeNote: "Terrain Lab candidate: boundary, terrain constraints, and POI layout.",
        }),
      });
      const revision = created?.revision;
      if (!revision?.id) throw new Error("candidate revision was not returned");
      this.persistence = {
        artifactId,
        baseRevisionId: revision.id,
        revisionNumber: revision.revisionNumber ?? revision.revision_number ?? null,
        status: revision.status ?? "candidate",
        loading: false,
        saving: false,
      };
      this.setStatus(`Saved candidate revision ${this.persistence.revisionNumber ?? ""}. It is not submitted or approved.`.trim());
      this.toast?.("Terrain candidate saved for review");
    } catch (error) {
      const message = String(error);
      this.persistence.status = /head|stale|changed/i.test(message) ? "stale" : "local";
      this.setStatus(this.persistence.status === "stale"
        ? "Terrain recipe changed while saving. Reload the current head before saving a new candidate."
        : `Could not save terrain candidate: ${message}`, true);
    } finally {
      this.persistence.saving = false;
      this.renderPersistenceStatus();
    }
  }

  activate() {
    if (this.active) return;
    this.active = true;
    document.body.classList.add("terrain-editing");
    const layout = this.getWorldData()?.layout?.doc;
    const worldSize = layout ? Math.max(layout.worldSizeCm.x, layout.worldSizeCm.y) / 100 : 1000;
    this.state = this.loadState(Math.max(worldSize, 600));
    this.ensureAuthoringState();
    this.history = [];
    this.persistence = { artifactId: null, baseRevisionId: null, revisionNumber: null, status: "local", loading: false, saving: false };
    this.freeCamera = false;
    this.renderShell();
    this.viewer.activateTerrainViewport({
      handlers: {
        pointermove: this._boundPointerMove,
        pointerdown: this._boundPointerDown,
        pointerup: this._boundPointerUp,
        pointercancel: this._boundPointerCancel,
        lostpointercapture: this._boundPointerCancel,
        keydown: this._boundKeyDown,
      },
      onNavigationChange: ({ looking, falling, input }) => {
        this.viewport.classList.toggle("terrain-looking", looking);
        const status = this.overlay?.querySelector("[data-terrain-nav]");
        if (!status) return;
        if (falling || input === "fall") status.textContent = "LANDING · GRAVITY ACTIVE";
        else if (looking) status.textContent = "LOOKING · RELEASE TO POINT";
        else if (input === "zoom") status.textContent = `PINCH ZOOM · ${Math.round(this.viewer.getTerrainCameraFov())}° VIEW`;
        else if (input === "glide") status.textContent = "TWO-FINGER GLIDE · SHIFT FAST";
        else status.textContent = "WASD · RMB / ⌥/CTRL + LMB LOOK · TWO-FINGER GLIDE";
      },
    });
    this.regenerate({ pois: !this.state.pois?.length, initial: true });
    this.loadRemoteRecipe();
    this.updateCameraModeUi();
  }

  deactivate({ restore = true } = {}) {
    if (!this.active) return;
    this.active = false;
    this.saveState();
    this.stopRecording({ discard: true });
    document.body.classList.remove("terrain-editing");
    this.viewport.classList.remove("terrain-looking");
    this.viewer.deactivateTerrainViewport();
    this.overlay?.remove();
    this.overlay = null;
    if (restore) this.onExit?.();
  }

  renderShell() {
    const list = this.sidebar.querySelector("#asset-list");
    list.innerHTML = `
      <div class="terrain-side-head">
        <button class="terrain-exit" type="button" data-terrain-action="exit">← World</button>
        <div class="terrain-eyebrow">PROCEDURAL FIELD LAB</div>
        <h2>Terrain is a system,<br><em>not a brush.</em></h2>
        <p>Compose landforms as rules. Every edit remains reproducible, inspectable, and reversible.</p>
      </div>
      <section class="terrain-presets" aria-label="Terrain presets">
        <div class="terrain-section-label">Starting geology</div>
        ${Object.entries(TERRAIN_PRESETS).map(([id, preset]) => `
          <button type="button" data-terrain-preset="${id}">
            <b>${this.esc(preset.label)}</b><span>${this.esc(preset.description)}</span>
          </button>`).join("")}
      </section>
      <section class="terrain-metrics" data-terrain-metrics></section>
      <section class="terrain-navigation-card">
        <div class="terrain-section-label">Field controls</div>
        <div><kbd>WASD</kbd><span>move over ground</span></div>
        <div><kbd>SHIFT</kbd><span>move faster</span></div>
        <div><kbd>RMB</kbd><span>hold and look</span></div>
        <div><kbd>⌥/CTRL + LMB</kbd><span>drag to look</span></div>
        <div><kbd>2-FINGER</kbd><span>glide over terrain</span></div>
        <div><kbd>PINCH</kbd><span>zoom the view</span></div>
        <div><kbd>LMB</kbd><span>point / drag a POI</span></div>
        <div><kbd>F</kbd><span>free camera / return to character</span></div>
      </section>`;

    const detailBody = this.detail.querySelector("#detail-body");
    detailBody.innerHTML = `
      <div class="terrain-rules-head">
        <div><span>PROCEDURE STACK</span><b data-terrain-rule-count></b></div>
        <div class="terrain-revision-actions">
          <span class="terrain-revision-status" data-terrain-revision-status>LOCAL PREVIEW</span>
          <button type="button" data-terrain-action="reload-recipe" title="Discard local preview changes and load the current recipe revision">RELOAD</button>
          <button type="button" data-terrain-action="save-candidate">SAVE CANDIDATE</button>
        </div>
        <button type="button" data-terrain-action="undo" title="Undo last procedure">↶ Undo</button>
        <button class="terrain-rules-close" type="button" data-terrain-action="close-rules" aria-label="Close rules">×</button>
      </div>
      <div class="terrain-rule-body" data-terrain-rule-body></div>`;

    this.overlay = document.createElement("div");
    this.overlay.className = "terrain-overlay";
    this.overlay.innerHTML = `
      <div class="terrain-view-status">
        <span class="terrain-live-dot"></span>
        <b data-terrain-nav>WASD · RMB / ⌥/CTRL + LMB LOOK · TWO-FINGER GLIDE</b>
        <span data-terrain-coordinate>0, 0 m</span>
        <button class="terrain-camera-mode" type="button" data-terrain-action="camera-mode" aria-pressed="false">FREE CAMERA · F</button>
        <button type="button" data-terrain-action="rules">RULES</button>
      </div>
      <div class="terrain-command-status" role="status" aria-live="polite" data-terrain-status>Point at the land, then describe a change.</div>
      <form class="terrain-command no-dictate" data-terrain-command>
        <div class="terrain-command-mark" aria-hidden="true">⌖</div>
        <label>
          <span>SPATIAL TERRAIN COMMAND</span>
          <input type="text" autocomplete="off" placeholder="Put a broad 90m mountain here…" aria-label="Terrain command">
        </label>
        <button class="terrain-mic" type="button" data-terrain-action="mic" aria-label="Speak a terrain command" title="Record; release by clicking again. The command auto-runs at your cursor.">●</button>
        <button class="terrain-send" type="submit">APPLY ↗</button>
      </form>
      <div class="terrain-quick-commands" aria-label="Quick terrain commands">
        <button type="button" data-command="Add a dramatic 110m mountain here">▲ Mountain</button>
        <button type="button" data-command="Carve a broad 24m deep lake here">◉ Lake</button>
        <button type="button" data-command="Cut a gentle valley here heading north">∨ Valley</button>
        <button type="button" data-command="Regenerate POIs from the rules">◆ Re-place POIs</button>
      </div>`;
    this.viewport.appendChild(this.overlay);

    this.sidebar.querySelector("[data-terrain-action='exit']").onclick = () => this.deactivate();
    this.sidebar.querySelectorAll("[data-terrain-preset]").forEach((button) => {
      button.onclick = () => this.applyPreset(button.dataset.terrainPreset);
    });
    this.detail.querySelector("[data-terrain-action='undo']").onclick = () => this.undo();
    this.detail.querySelector("[data-terrain-action='reload-recipe']").onclick = () => this.loadRemoteRecipe();
    this.detail.querySelector("[data-terrain-action='save-candidate']").onclick = () => this.saveCandidate();
    this.detail.querySelector("[data-terrain-action='close-rules']").onclick = () => this.detail.classList.remove("mobile-open");
    this.detail.querySelector("#mobile-detail-close").addEventListener("click", () => this.detail.classList.remove("mobile-open"), { once: true });
    this.overlay.querySelector("[data-terrain-command]").onsubmit = (event) => {
      event.preventDefault();
      const input = event.currentTarget.querySelector("input");
      this.execute(input.value, this.anchor);
      input.value = "";
    };
    this.overlay.querySelector("[data-terrain-action='mic']").onclick = () => this.toggleRecording();
    this.overlay.querySelector("[data-terrain-action='camera-mode']").onclick = () => this.setFreeCamera(!this.freeCamera);
    this.overlay.querySelector("[data-terrain-action='rules']").onclick = () => this.detail.classList.add("mobile-open");
    this.overlay.querySelectorAll("[data-command]").forEach((button) => {
      button.onclick = () => this.execute(button.dataset.command, this.anchor);
    });
    this.renderInspector();
    this.renderPersistenceStatus();
  }

  walkModeOptions(dropToGround = false) {
    return {
      heightAt: (x, z) => sampleTerrain(this.terrain, x, z),
      halfSize: this.state.size / 2,
      eyeHeight: 1.75,
      speed: Math.max(18, this.state.size * 0.026),
      dropToGround,
    };
  }

  setFreeCamera(on) {
    if (!this.active || !this.terrain) return;
    const nextFreeCamera = Boolean(on);
    if (nextFreeCamera === this.freeCamera) return;
    this.freeCamera = nextFreeCamera;
    if (this.freeCamera) {
      this.viewer.setTerrainCameraMode("free", { speed: terrainFreeCameraSpeed(this.state.size) });
      this.setStatus("Free camera detached. The character stays here; press F to return.");
    } else {
      this.viewer.setTerrainCameraMode("walk", this.walkModeOptions(false));
      this.setStatus("Returned to the character at the saved position and view.");
    }
    this.updateCameraModeUi();
  }

  updateCameraModeUi() {
    const button = this.overlay?.querySelector("[data-terrain-action='camera-mode']");
    if (!button) return;
    button.classList.toggle("on", this.freeCamera);
    button.setAttribute("aria-pressed", String(this.freeCamera));
    button.textContent = this.freeCamera ? "RETURN TO CHARACTER · F" : "FREE CAMERA · F";
    if (this.freeCamera) {
      const status = this.overlay?.querySelector("[data-terrain-nav]");
      if (status) status.textContent = "FREE CAMERA · WASD · SPACE UP · CTRL DOWN · F TO RETURN";
    }
  }

  onKeyDown(event) {
    if (!this.active || event.code !== "KeyF" || event.repeat) return;
    if (event.target?.matches?.("input, textarea, select, [contenteditable='true']")) return;
    event.preventDefault();
    this.setFreeCamera(!this.freeCamera);
  }

  renderPersistenceStatus() {
    const status = this.detail?.querySelector("[data-terrain-revision-status]");
    const button = this.detail?.querySelector("[data-terrain-action='save-candidate']");
    const reload = this.detail?.querySelector("[data-terrain-action='reload-recipe']");
    if (!status || !button || !reload) return;
    const revision = this.persistence.revisionNumber ? `R${this.persistence.revisionNumber}` : "LOCAL";
    const label = this.persistence.loading ? "LOADING" : this.persistence.saving ? "SAVING" : this.persistence.status === "stale" ? "STALE" : `${revision} · ${String(this.persistence.status || "preview").toUpperCase()}`;
    status.textContent = label;
    status.classList.toggle("stale", this.persistence.status === "stale");
    button.disabled = this.persistence.loading || this.persistence.saving;
    button.textContent = this.persistence.saving ? "SAVING…" : "SAVE CANDIDATE";
    reload.disabled = this.persistence.loading || this.persistence.saving;
  }

  applyPreset(id) {
    const previousSize = this.state.size;
    const terrainUid = this.state.terrainUid;
    this.pushHistory();
    this.state = createTerrainState({ size: previousSize, preset: id });
    this.state.terrainUid = terrainUid;
    this.selectedPoiId = null;
    this.regenerate({ pois: true });
    this.setStatus(`${TERRAIN_PRESETS[id]?.label ?? "Terrain"} procedure stack loaded.`);
  }

  pushHistory() {
    this.history.push(cloneTerrainState(this.state));
    if (this.history.length > 24) this.history.shift();
  }

  undo() {
    const previous = this.history.pop();
    if (!previous) return this.setStatus("Nothing to undo yet.", true);
    this.state = previous;
    this.regenerate({ pois: false });
    this.setStatus("Restored the previous procedure stack.");
  }

  execute(text, anchor) {
    const heading = this.viewer.getTerrainCameraHeading();
    const result = applyTerrainCommand(this.state, text, anchor, heading);
    if (!result.changed) return this.setStatus(result.message, true);
    this.pushHistory();
    this.state = result.state;
    this.regenerate({ pois: result.regeneratePois !== false });
    this.setStatus(result.message);
  }

  setStatus(message, warning = false) {
    const node = this.overlay?.querySelector("[data-terrain-status]");
    if (!node) return;
    node.textContent = message;
    node.classList.toggle("warning", warning);
  }

  scheduleRegenerate({ pois = true, renderInspector = false } = {}) {
    this._scheduledPois = this._scheduledPois || pois;
    this._scheduledRenderInspector = this._scheduledRenderInspector || renderInspector;
    if (this._regenerateFrame) return;
    const run = () => {
      this._regenerateFrame = null;
      const regeneratePois = this._scheduledPois;
      const shouldRenderInspector = this._scheduledRenderInspector;
      this._scheduledPois = false;
      this._scheduledRenderInspector = false;
      this.regenerate({ pois: regeneratePois, renderInspector: shouldRenderInspector });
    };
    this._regenerateFrame = typeof requestAnimationFrame === "function" ? requestAnimationFrame(run) : setTimeout(run, 0);
  }

  updateLiveRange(input, apply, { pois = true, unit = "" } = {}) {
    if (!input.dataset.historyPushed) {
      this.pushHistory();
      input.dataset.historyPushed = "true";
    }
    const value = Number(input.value);
    apply(value);
    const output = input.closest("label")?.querySelector("[data-range-output]");
    if (output) output.textContent = `${value.toFixed(Number(input.step) < 1 ? 2 : 0)}${unit}`;
    this.scheduleRegenerate({ pois });
  }

  async toggleRecording() {
    if (this.recorder?.state === "recording") return this.stopRecording();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordChunks = [];
      this.recordAnchor = { ...this.anchor };
      this.recorder = new MediaRecorder(stream);
      this.recorder.ondataavailable = (event) => { if (event.data.size) this.recordChunks.push(event.data); };
      this.recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const button = this.overlay?.querySelector("[data-terrain-action='mic']");
        button?.classList.remove("recording");
        if (!this.recordChunks.length) return;
        this.setStatus("Transcribing spatial command…");
        const blob = new Blob(this.recordChunks, { type: this.recorder?.mimeType || "audio/webm" });
        const file = new File([blob], `terrain-command-${Date.now()}.webm`, { type: blob.type });
        try {
          const transcript = await this.transcribe(file);
          if (!transcript?.trim()) return this.setStatus("No speech was detected.", true);
          const input = this.overlay?.querySelector("[data-terrain-command] input");
          if (input) input.value = transcript;
          this.execute(transcript, this.recordAnchor ?? this.anchor);
          if (input) input.value = "";
        } catch (error) {
          this.setStatus(`Microphone command failed: ${String(error)}`, true);
        }
      };
      this.recorder.start();
      this.overlay.querySelector("[data-terrain-action='mic']")?.classList.add("recording");
      this.setStatus("Listening… keep pointing at the place you mean, then tap the mic again.");
    } catch (error) {
      this.setStatus(`Microphone unavailable: ${String(error)}`, true);
    }
  }

  stopRecording({ discard = false } = {}) {
    if (this.recorder?.state !== "recording") return;
    if (discard) this.recordChunks = [];
    this.recorder.stop();
  }

  regenerate({ pois = true, initial = false, renderInspector = true } = {}) {
    const cameraPose = this.viewer.captureTerrainCameraPose();
    this.terrain = generateTerrain(this.state, pois ? { poiPads: [] } : undefined);
    if (pois) {
      this.state.pois = generatePois(this.state, this.terrain);
      this.terrain = generateTerrain(this.state, { poiPads: this.state.pois });
    }
    this.state.pois = (this.state.pois ?? []).map((poi) => ({
      ...poi,
      y: sampleTerrain(this.terrain, poi.x, poi.z),
      slope: terrainSlope(this.terrain, poi.x, poi.z),
    }));
    this.buildScene();
    this.saveState();
    if (renderInspector) this.renderInspector();
    this.renderMetrics();
    if (initial) {
      const startZ = this.state.size * 0.28;
      const startHeight = sampleTerrain(this.terrain, 0, startZ);
      this.viewer.setTerrainCameraLookAt([0, startHeight + 34, startZ], [0, sampleTerrain(this.terrain, 0, 0) + 9, 0]);
    } else {
      this.viewer.setTerrainCameraPose(cameraPose);
    }
    if (this.freeCamera) {
      this.viewer.setTerrainCameraMode("free", { speed: terrainFreeCameraSpeed(this.state.size) });
    } else {
      this.viewer.setTerrainCameraMode("walk", this.walkModeOptions(false));
    }
    this.updateCameraModeUi();
  }

  buildScene() {
    const group = new THREE.Group();
    group.name = "Procedural terrain editor";
    const terrainMesh = this.makeTerrainMesh();
    group.add(terrainMesh);
    this.terrainMesh = terrainMesh;

    for (const feature of this.state.features) {
      if (feature.type === "lake") group.add(this.makeLake(feature));
      if (feature.type === "river") group.add(this.makeRiver(feature));
    }

    this.boundaryGroup = this.makeBoundaryControls();
    group.add(this.boundaryGroup);
    this.heightControlGroup = this.makeHeightControls();
    group.add(this.heightControlGroup);

    this.poiGroup = new THREE.Group();
    this.poiGroup.name = "Rule-based POIs";
    for (const poi of this.state.pois) this.poiGroup.add(this.makePoi(poi));
    group.add(this.poiGroup);

    this.reticle = new THREE.Mesh(
      new THREE.RingGeometry(5.5, 6.4, 40),
      new THREE.MeshBasicMaterial({ color: 0xffb24c, side: THREE.DoubleSide, depthTest: false, transparent: true, opacity: 0.95 }),
    );
    this.reticle.rotation.x = -Math.PI / 2;
    this.reticle.renderOrder = 20;
    const anchorHeight = sampleTerrain(this.terrain, this.anchor.x, this.anchor.z);
    this.reticle.position.set(this.anchor.x, anchorHeight + 0.35, this.anchor.z);
    group.add(this.reticle);

    this.group = group;
    this.viewer.replaceTerrainScene({
      group,
      terrainMesh,
      terrain: this.terrain,
      state: this.state,
      clipping: { near: 0.5, far: Math.max(4000, this.state.size * 5) },
    });
  }

  makeBoundaryControls() {
    const group = new THREE.Group();
    group.name = "Terrain boundary controls";
    const boundary = this.state.boundary ?? {};
    const fallback = [
      { id: "outer_1", x: -this.state.size / 2, z: -this.state.size / 2 },
      { id: "outer_2", x: this.state.size / 2, z: -this.state.size / 2 },
      { id: "outer_3", x: this.state.size / 2, z: this.state.size / 2 },
      { id: "outer_4", x: -this.state.size / 2, z: this.state.size / 2 },
    ];
    const rings = [Array.isArray(boundary.outer) && boundary.outer.length >= 3 ? boundary.outer : fallback, ...(boundary.holes ?? [])];
    rings.forEach((ring, ringIndex) => {
      const points = ring.map((point) => new THREE.Vector3(point.x, sampleTerrain(this.terrain, point.x, point.z) + 0.7, point.z));
      const loop = new THREE.LineLoop(
        new THREE.BufferGeometry().setFromPoints(points),
        new THREE.LineBasicMaterial({ color: ringIndex ? 0x71b7c0 : 0xca8640, transparent: true, opacity: 0.88 }),
      );
      loop.userData.boundaryLoop = ringIndex;
      group.add(loop);
      ring.forEach((point, pointIndex) => {
        const y = sampleTerrain(this.terrain, point.x, point.z);
        const handle = new THREE.Mesh(
          new THREE.SphereGeometry(ringIndex ? 2.4 : 2.9, 12, 9),
          new THREE.MeshStandardMaterial({ color: ringIndex ? 0x73c1cb : 0xf2a44f, emissive: ringIndex ? 0x24565d : 0x61330e, emissiveIntensity: 0.65, roughness: 0.45 }),
        );
        handle.position.set(point.x, y + 1.45, point.z);
        handle.userData.boundaryPoint = { ringIndex, pointIndex, id: point.id };
        group.add(handle);
      });
    });
    return group;
  }

  makeHeightControls() {
    const group = new THREE.Group();
    group.name = "Manual terrain height controls";
    for (const control of this.state.manualHeightControls ?? []) {
      const y = sampleTerrain(this.terrain, control.x, control.z);
      const marker = new THREE.Group();
      marker.position.set(control.x, y + 1.2, control.z);
      marker.userData.heightControlId = control.id;
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.45, 0.75, 10, 8),
        new THREE.MeshStandardMaterial({ color: 0x9c74ca, emissive: 0x311a4d, emissiveIntensity: 0.5, roughness: 0.42 }),
      );
      stem.position.y = 5;
      stem.userData.heightControlId = control.id;
      marker.add(stem);
      const cap = new THREE.Mesh(
        new THREE.OctahedronGeometry(2.5, 0),
        new THREE.MeshStandardMaterial({ color: 0xd0b1ed, emissive: 0x5c2b89, emissiveIntensity: 0.5, roughness: 0.35 }),
      );
      cap.position.y = 10.5;
      cap.userData.heightControlId = control.id;
      marker.add(cap);
      group.add(marker);
    }
    return group;
  }

  makeTerrainMesh() {
    const { resolution, size, heights, min, max, inside: mask } = this.terrain;
    const count = resolution * resolution;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const half = size / 2;
    const cell = size / (resolution - 1);
    const low = new THREE.Color(0x3f5543);
    const middle = new THREE.Color(0x777154);
    const high = new THREE.Color(0xc0bba8);
    const waterbed = new THREE.Color(0x314d4c);
    const climbableRock = new THREE.Color(0x9a705b);
    const blockedRock = new THREE.Color(0x35434a);
    const color = new THREE.Color();
    for (let row = 0; row < resolution; row++) {
      for (let column = 0; column < resolution; column++) {
        const index = row * resolution + column;
        const x = -half + column * cell;
        const z = -half + row * cell;
        const y = heights[index];
        positions[index * 3] = x;
        positions[index * 3 + 1] = y;
        positions[index * 3 + 2] = z;
        const normalized = (y - min) / Math.max(max - min, 1);
        const traversal = classifyTraversal(this.terrain, this.state, x, z);
        if (traversal.mode === "water") color.copy(waterbed);
        else if (traversal.mode === "blocked") color.copy(blockedRock);
        else if (traversal.mode === "climbable") color.copy(climbableRock);
        else if (normalized < 0.5) color.copy(low).lerp(middle, normalized * 2);
        else color.copy(middle).lerp(high, (normalized - 0.5) * 2);
        const contourDistance = Math.abs(((y - this.state.waterLevel + 5000) % 10) - 5);
        if (contourDistance > 4.25) color.multiplyScalar(0.72);
        colors[index * 3] = color.r;
        colors[index * 3 + 1] = color.g;
        colors[index * 3 + 2] = color.b;
      }
    }
    const indexList = [];
    let cursor = 0;
    for (let row = 0; row < resolution - 1; row++) {
      for (let column = 0; column < resolution - 1; column++) {
        if (!this.terrainCellIsInside(mask, resolution, row, column)) continue;
        const a = row * resolution + column, b = a + 1, c = a + resolution, d = c + 1;
        indexList[cursor++] = a; indexList[cursor++] = c; indexList[cursor++] = b;
        indexList[cursor++] = b; indexList[cursor++] = c; indexList[cursor++] = d;
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(indexList);
    geometry.computeVertexNormals();
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.96, metalness: 0, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.receiveShadow = true;
    mesh.userData.terrainSurface = true;
    return mesh;
  }

  terrainCellIsInside(mask, resolution, row, column) {
    if (!mask) return true;
    const cellsWide = resolution - 1;
    if (mask.length === cellsWide * cellsWide) return Boolean(mask[row * cellsWide + column]);
    if (mask.length === resolution * resolution) {
      const a = row * resolution + column;
      return Boolean(mask[a]) && Boolean(mask[a + 1]) && Boolean(mask[a + resolution]) && Boolean(mask[a + resolution + 1]);
    }
    return true;
  }

  makeLake(feature) {
    const centerHeight = sampleTerrain(this.terrain, feature.x, feature.z);
    const waterHeight = Math.max(this.state.waterLevel, centerHeight + feature.depth * 0.68);
    const mesh = new THREE.Mesh(
      new THREE.CircleGeometry(feature.radius * 0.89, 64),
      new THREE.MeshPhysicalMaterial({ color: 0x245f68, roughness: 0.18, metalness: 0.05, transparent: true, opacity: 0.8, transmission: 0.08 }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(feature.x, waterHeight, feature.z);
    mesh.userData.water = true;
    return mesh;
  }

  makeRiver(feature) {
    const centerHeight = sampleTerrain(this.terrain, feature.x, feature.z) + (feature.depth ?? 18) * 0.55;
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(feature.length, feature.radius * 1.35, 40, 1),
      new THREE.MeshPhysicalMaterial({ color: 0x2c7077, roughness: 0.25, transparent: true, opacity: 0.76, side: THREE.DoubleSide }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = -(feature.angle ?? 0);
    mesh.position.set(feature.x, centerHeight, feature.z);
    return mesh;
  }

  makePoi(poi) {
    const color = POI_COLORS[poi.type] ?? 0xf0a64a;
    const group = new THREE.Group();
    group.position.set(poi.x, poi.y + 0.5, poi.z);
    group.userData.poiId = poi.id;
    const pin = new THREE.Mesh(
      new THREE.ConeGeometry(2.3, 7, 5),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.16, roughness: 0.7 }),
    );
    pin.rotation.x = Math.PI;
    pin.position.y = 4.2;
    pin.userData.poiId = poi.id;
    group.add(pin);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(3.3, 4.2, 24),
      new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, transparent: true, opacity: poi.manual ? 1 : 0.7 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.15;
    ring.userData.poiId = poi.id;
    group.add(ring);
    const label = makePoiLabel(poi.label, color);
    label.userData.poiId = poi.id;
    group.add(label);
    return group;
  }

  renderMetrics() {
    const node = this.sidebar.querySelector("[data-terrain-metrics]");
    if (!node || !this.terrain) return;
    const range = this.terrain.max - this.terrain.min;
    const manual = this.state.pois.filter((poi) => poi.manual).length + (this.state.manualHeightControls?.length ?? 0);
    node.innerHTML = `
      <div><span>Vertical range</span><b>${Math.round(range)} m</b></div>
      <div><span>Rule operators</span><b>${this.state.features.length}</b></div>
      <div><span>POI candidates</span><b>${this.state.pois.length}</b></div>
      <div><span>Manual overrides</span><b>${manual}</b></div>`;
  }

  renderInspector() {
    const body = this.detail.querySelector("[data-terrain-rule-body]");
    if (!body || !this.state) return;
    this.detail.querySelector("[data-terrain-rule-count]").textContent = `${this.state.features.length} terrain · ${this.state.pois.length} POI`;
    const selected = this.state.pois.find((poi) => poi.id === this.selectedPoiId);
    const traversal = this.state.traversal ?? {};
    const constraints = this.state.constraints ?? {};
    const defaultRule = this.state.poiRules?.[0] ?? {};
    const selectedHeight = this.state.manualHeightControls?.find((control) => control.id === this.selectedHeightControlId);
    body.innerHTML = `
      <section class="terrain-live-controls" aria-label="Live terrain controls">
        <div class="terrain-section-label">Live terrain constraints</div>
        ${rangeField({ label: "Roughness", key: "roughness", value: this.state.roughness ?? 0.5, min: 0, max: 1, step: 0.01 })}
        ${rangeField({ label: "Water level", key: "water", value: this.state.waterLevel ?? 0, min: -80, max: 120, step: 1, unit: "m" })}
        ${rangeField({ label: "Erosion passes", key: "erosion", value: this.state.erosion ?? 0, min: 0, max: 12, step: 1 })}
        ${rangeField({ label: "Walkable slope", key: "walkable", value: traversal.walkableMaxSlopeDeg ?? constraints.walkableMaxSlopeDeg ?? 30, min: 5, max: 55, step: 1, unit: "°" })}
        ${rangeField({ label: "Climbable slope", key: "climbable", value: traversal.climbableMaxSlopeDeg ?? constraints.climbableMaxSlopeDeg ?? 45, min: 10, max: 75, step: 1, unit: "°" })}
        ${rangeField({ label: "POI spacing", key: "poi-spacing", value: defaultRule.minSpacing ?? 80, min: 20, max: Math.max(60, this.state.size * 0.5), step: 1, unit: "m" })}
        <div class="terrain-traversal-legend"><span><i class="walkable"></i>walkable</span><span><i class="climbable"></i>climbable</span><span><i class="blocked"></i>blocked</span><span><i class="water"></i>water</span></div>
        <p>Drag the amber outer boundary or blue inner boundary handles to reshape the playable terrain. The centre void is a real landscape exclusion, not a painted square.</p>
      </section>
      <section class="terrain-height-controls">
        <div class="terrain-section-label">Manual elevation pins</div>
        <button type="button" data-terrain-action="add-height-pin">＋ Add height pin at reticle</button>
        ${(this.state.manualHeightControls ?? []).map((control) => `<button type="button" class="${control.id === this.selectedHeightControlId ? "selected" : ""}" data-height-select="${this.esc(control.id)}"><b>${this.esc(control.id.replaceAll("_", " "))}</b><span>${Math.round(control.height)}m · ${Math.round(control.radius)}m radius</span></button>`).join("") || `<p>Pin a local summit, terrace, valley floor, or POI pad. Pins are applied after procedural generation.</p>`}
        ${selectedHeight ? `<div class="terrain-height-pin-editor">
          <label>Target elevation<input type="number" step="1" value="${selectedHeight.height}" data-height-field="height"></label>
          <label>Radius<input type="number" min="1" step="1" value="${selectedHeight.radius}" data-height-field="radius"></label>
          <label>Blend<input type="number" min="0" step="1" value="${selectedHeight.blend ?? 0}" data-height-field="blend"></label>
          <button type="button" data-height-remove="${this.esc(selectedHeight.id)}">Remove pin</button>
        </div>` : ""}
      </section>
      ${selected ? `<section class="terrain-selected-poi">
        <div class="terrain-section-label">Selected POI</div>
        <h3>${this.esc(selected.label)} ${selected.manual ? `<span>MANUAL</span>` : ""}</h3>
        <p>${Math.round(selected.x)}, ${Math.round(selected.z)} m · ${selected.slope.toFixed(1)}° slope</p>
        <div class="terrain-poi-inspector-grid">
          <label>Footprint<input type="number" min="8" max="500" step="1" value="${selected.radius ?? selected.footprintRadius ?? 32}" data-selected-poi-field="radius"></label>
          <label>Flat pad<input type="number" min="8" max="500" step="1" value="${Math.max(selected.radius ?? selected.footprintRadius ?? 32, selected.padRadius ?? selected.flatPadRadius ?? 32)}" data-selected-poi-field="padRadius"></label>
          <label>Max slope<input type="number" min="1" max="75" step="1" value="${selected.maxSlope ?? selected.slopeLimit ?? defaultRule.maxSlope ?? 18}" data-selected-poi-field="maxSlope"></label>
          <label>Spacing<input type="number" min="10" max="1000" step="1" value="${selected.minSpacing ?? selected.spacing ?? defaultRule.minSpacing ?? 80}" data-selected-poi-field="minSpacing"></label>
        </div>
        <button type="button" data-poi-reset="${this.esc(selected.id)}">Return to rule placement</button>
      </section>` : ""}
      <section class="terrain-rule-list">
        ${this.state.features.map((feature, index) => {
          const magnitudeKey = ["mountain", "ridge", "plateau"].includes(feature.type) ? "height" : "depth";
          const magnitude = feature[magnitudeKey] ?? 20;
          return `<article class="terrain-rule-card" data-feature-id="${this.esc(feature.id)}">
            <button class="terrain-rule-locate" type="button" data-feature-locate="${this.esc(feature.id)}"><span>${featureIcon(feature.type)}</span></button>
            <div class="terrain-rule-copy"><small>0${index + 1} · ${this.esc(feature.type.toUpperCase())}</small><b>${this.esc(feature.id.replaceAll("_", " "))}</b><p>${this.esc(formatFeatureSummary(feature))}</p></div>
            <button class="terrain-rule-remove" type="button" data-feature-remove="${this.esc(feature.id)}" aria-label="Remove ${this.esc(feature.type)}">×</button>
            <label><span>${magnitudeKey}</span><input type="range" min="2" max="${magnitudeKey === "height" ? 180 : 80}" value="${magnitude}" data-feature-value="${this.esc(feature.id)}:${magnitudeKey}"></label>
            <label><span>span</span><input type="range" min="30" max="${Math.round(this.state.size * 0.6)}" value="${Math.round(feature.radius * 2)}" data-feature-value="${this.esc(feature.id)}:span"></label>
          </article>`;
        }).join("") || `<p class="terrain-empty-rules">No landform procedures. Point at the terrain and ask for one.</p>`}
      </section>
      <section class="terrain-poi-rules">
        <div class="terrain-section-label">POI placement rules</div>
        ${this.state.poiRules.map((rule) => `<label>
          <span><i style="--poi-color:#${(POI_COLORS[rule.type] ?? 0xffffff).toString(16).padStart(6, "0")}"></i><b>${this.esc(rule.label)}</b><small>≤ ${rule.maxSlope}° · ${rule.minSpacing}m clearance</small></span>
          <input type="number" min="1" max="24" value="${rule.count}" data-poi-rule="${this.esc(rule.id)}" aria-label="${this.esc(rule.label)} count">
        </label>`).join("")}
        <button class="terrain-reseed-pois" type="button" data-terrain-action="reseed-pois">↻ Re-apply placement rules</button>
        <p>Drag any indicator to create a manual override. Automatic sites continue to honor slope, water access, and spacing.</p>
      </section>`;

    body.querySelectorAll("[data-feature-remove]").forEach((button) => {
      button.onclick = () => {
        this.pushHistory();
        this.state.features = this.state.features.filter((feature) => feature.id !== button.dataset.featureRemove);
        this.regenerate({ pois: true });
      };
    });
    body.querySelectorAll("[data-feature-locate]").forEach((button) => {
      button.onclick = () => this.focusFeature(button.dataset.featureLocate);
    });
    body.querySelectorAll("[data-feature-value]").forEach((input) => {
      input.oninput = () => {
        const [id, key] = input.dataset.featureValue.split(":");
        const feature = this.state.features.find((candidate) => candidate.id === id);
        if (!feature) return;
        if (!input.dataset.historyPushed) {
          this.pushHistory();
          input.dataset.historyPushed = "true";
        }
        if (key === "span") feature.radius = Number(input.value) / 2;
        else feature[key] = Number(input.value);
        this.scheduleRegenerate({ pois: true });
      };
      input.onchange = () => { delete input.dataset.historyPushed; this.renderInspector(); };
    });
    body.querySelectorAll("[data-poi-rule]").forEach((input) => {
      input.onchange = () => {
        const rule = this.state.poiRules.find((candidate) => candidate.id === input.dataset.poiRule);
        if (!rule) return;
        this.pushHistory();
        rule.count = Math.max(1, Math.min(24, Number(input.value) || 1));
        this.regenerate({ pois: true });
      };
    });
    body.querySelector("[data-terrain-action='reseed-pois']")?.addEventListener("click", () => {
      this.pushHistory();
      this.state.poiSeed = ((this.state.poiSeed ?? this.state.seed) + 7919) % 2147483647;
      this.regenerate({ pois: true });
      this.setStatus("POI candidates regenerated against the current rules.");
    });
    body.querySelector("[data-terrain-action='add-height-pin']")?.addEventListener("click", () => {
      this.pushHistory();
      const controls = this.state.manualHeightControls;
      let index = controls.length + 1;
      while (controls.some((control) => control.id === `height_pin_${index}`)) index++;
      const id = `height_pin_${index}`;
      const height = sampleTerrain(this.terrain, this.anchor.x, this.anchor.z);
      controls.push({ id, x: this.anchor.x, z: this.anchor.z, height: Math.round(height), radius: 28, blend: 12 });
      this.selectedHeightControlId = id;
      this.regenerate({ pois: true });
      this.setStatus("Added a manual height pin at the reticle. Adjust its target elevation and blend below.");
    });
    body.querySelectorAll("[data-height-select]").forEach((button) => {
      button.onclick = () => {
        this.selectedHeightControlId = button.dataset.heightSelect;
        this.renderInspector();
      };
    });
    body.querySelectorAll("[data-height-field]").forEach((input) => {
      input.onchange = () => {
        const control = this.state.manualHeightControls.find((candidate) => candidate.id === this.selectedHeightControlId);
        if (!control) return;
        this.pushHistory();
        const field = input.dataset.heightField;
        const value = Number(input.value) || 0;
        control[field] = field === "radius" ? Math.max(1, value) : field === "blend" ? Math.max(0, value) : value;
        this.regenerate({ pois: true });
      };
    });
    body.querySelector("[data-height-remove]")?.addEventListener("click", () => {
      this.pushHistory();
      this.state.manualHeightControls = this.state.manualHeightControls.filter((control) => control.id !== this.selectedHeightControlId);
      this.selectedHeightControlId = null;
      this.regenerate({ pois: true });
    });
    body.querySelector("[data-poi-reset]")?.addEventListener("click", () => {
      this.pushHistory();
      this.state.pois = this.state.pois.filter((poi) => poi.id !== selected.id);
      this.selectedPoiId = null;
      this.regenerate({ pois: true });
    });

    const rangeUpdates = {
      roughness: (value) => { this.state.roughness = value; },
      water: (value) => { this.state.waterLevel = value; },
      erosion: (value) => { this.state.erosion = value; },
      walkable: (value) => {
        const climbable = Math.max(this.state.traversal?.climbableMaxSlopeDeg ?? 52, value + 1);
        this.state.traversal = { ...(this.state.traversal ?? {}), walkableMaxSlopeDeg: value, climbableMaxSlopeDeg: climbable };
        this.state.constraints = { ...(this.state.constraints ?? {}), walkableMaxSlopeDeg: value };
      },
      climbable: (value) => {
        const walkable = Math.min(this.state.traversal?.walkableMaxSlopeDeg ?? 32, value - 1);
        this.state.traversal = { ...(this.state.traversal ?? {}), walkableMaxSlopeDeg: walkable, climbableMaxSlopeDeg: value };
        this.state.constraints = { ...(this.state.constraints ?? {}), climbableMaxSlopeDeg: value };
      },
      "poi-spacing": (value) => {
        this.state.poiRules.forEach((rule) => { rule.minSpacing = value; });
      },
    };
    body.querySelectorAll("[data-terrain-range]").forEach((input) => {
      input.oninput = () => this.updateLiveRange(input, rangeUpdates[input.dataset.terrainRange], {
        pois: input.dataset.terrainRange !== "water",
        unit: ["water", "poi-spacing"].includes(input.dataset.terrainRange) ? "m" : ["walkable", "climbable"].includes(input.dataset.terrainRange) ? "°" : "",
      });
      input.onchange = () => { delete input.dataset.historyPushed; this.renderInspector(); };
    });
    body.querySelectorAll("[data-selected-poi-field]").forEach((input) => {
      input.onchange = () => {
        const poi = this.state.pois.find((candidate) => candidate.id === this.selectedPoiId);
        if (!poi) return;
        this.pushHistory();
        const field = input.dataset.selectedPoiField;
        const value = Math.max(field === "maxSlope" ? 1 : 0, Number(input.value) || 0);
        if (field === "radius") {
          poi.radius = value;
          poi.padRadius = Math.max(poi.padRadius ?? value, value);
        } else if (field === "padRadius") poi.padRadius = Math.max(value, poi.radius ?? 0);
        else poi[field] = value;
        poi.manual = true;
        poi.pinned = true;
        this.regenerate({ pois: false });
        this.setStatus(`${poi.label} now has a pinned local placement constraint.`);
      };
    });
  }

  focusFeature(id) {
    const feature = this.state.features.find((candidate) => candidate.id === id);
    if (!feature) return;
    this.anchor = { x: feature.x, z: feature.z };
    const y = sampleTerrain(this.terrain, feature.x, feature.z);
    this.reticle.position.set(feature.x, y + 0.35, feature.z);
    this.viewer.setTerrainCameraLookAt(
      [feature.x + feature.radius * 1.2, y + Math.max(45, feature.radius * 0.45), feature.z + feature.radius * 1.2],
      [feature.x, y, feature.z],
    );
  }

  hitTerrain(event) {
    if (!this.terrainMesh) return null;
    this.viewer.setTerrainRayFromPointer(event, this.raycaster);
    return this.raycaster.intersectObject(this.terrainMesh, false)[0] ?? null;
  }

  hitGroundPlane(event) {
    this.viewer.setTerrainRayFromPointer(event, this.raycaster);
    const point = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), point) ? { point } : null;
  }

  updateBoundaryPoint({ ringIndex, pointIndex, id }, point) {
    const boundary = this.state.boundary;
    const ring = ringIndex === 0 ? boundary?.outer : boundary?.holes?.[ringIndex - 1];
    const target = ring?.[pointIndex];
    if (!target || target.id !== id) return;
    const half = this.state.size / 2;
    target.x = THREE.MathUtils.clamp(point.x, -half, half);
    target.z = THREE.MathUtils.clamp(point.z, -half, half);
    target.manual = true;
    this.scheduleRegenerate({ pois: true });
  }

  onPointerMove(event) {
    if (!this.active || this.viewer.isTerrainLooking()) return;
    const hit = this.hitTerrain(event) ?? (this.draggingBoundaryPoint ? this.hitGroundPlane(event) : null);
    if (!hit) return;
    this.anchor = { x: hit.point.x, z: hit.point.z };
    if (this.recorder?.state === "recording") this.recordAnchor = { ...this.anchor };
    if (this.reticle) this.reticle.position.set(hit.point.x, hit.point.y + 0.35, hit.point.z);
    const coordinate = this.overlay?.querySelector("[data-terrain-coordinate]");
    if (coordinate) coordinate.textContent = `${Math.round(hit.point.x)}, ${Math.round(hit.point.z)} m · ${Math.round(hit.point.y)}m elev`;
    if (this.draggingBoundaryPoint) {
      this.updateBoundaryPoint(this.draggingBoundaryPoint, hit.point);
      return;
    }
    if (!this.draggingPoiId) return;
    const poi = this.state.pois.find((candidate) => candidate.id === this.draggingPoiId);
    const object = this.poiGroup.children.find((candidate) => candidate.userData.poiId === this.draggingPoiId);
    if (!poi || !object) return;
    poi.x = hit.point.x; poi.z = hit.point.z; poi.y = hit.point.y; poi.slope = terrainSlope(this.terrain, poi.x, poi.z);
    object.position.set(poi.x, poi.y + 0.5, poi.z);
  }

  onPointerDown(event) {
    if (!this.active || event.button !== 0 || event.altKey || event.ctrlKey || this.viewer.isTerrainLooking()) return;
    this.viewer.setTerrainRayFromPointer(event, this.raycaster);
    const heightHit = this.heightControlGroup ? this.raycaster.intersectObject(this.heightControlGroup, true)[0] : null;
    if (heightHit) {
      let object = heightHit.object;
      while (object && !object.userData.heightControlId) object = object.parent;
      if (object?.userData.heightControlId) {
        event.preventDefault();
        this.selectedHeightControlId = object.userData.heightControlId;
        this.selectedPoiId = null;
        this.renderInspector();
        this.detail.classList.add("mobile-open");
        this.setStatus("Selected a manual height pin. Its values are editable in the rule panel.");
        return;
      }
    }
    const boundaryHit = this.boundaryGroup ? this.raycaster.intersectObject(this.boundaryGroup, true)[0] : null;
    if (boundaryHit) {
      let object = boundaryHit.object;
      while (object && !object.userData.boundaryPoint) object = object.parent;
      if (object?.userData.boundaryPoint) {
        event.preventDefault();
        this.pushHistory();
        this.draggingBoundaryPoint = { ...object.userData.boundaryPoint };
        this.viewer.captureTerrainPointer(event.pointerId);
        this.viewport.classList.add("terrain-dragging-boundary");
        this.setStatus("Dragging terrain boundary · release to pin this shape control.");
        return;
      }
    }
    const poiHit = this.raycaster.intersectObject(this.poiGroup, true)[0];
    if (poiHit) {
      let object = poiHit.object;
      while (object && !object.userData.poiId) object = object.parent;
      if (object?.userData.poiId) {
        event.preventDefault();
        this.pushHistory();
        this.draggingPoiId = object.userData.poiId;
        this.selectedPoiId = object.userData.poiId;
        this.viewer.captureTerrainPointer(event.pointerId);
        this.viewport.classList.add("terrain-dragging-poi");
        this.setStatus("Dragging POI · release to create a manual override.");
      }
    }
  }

  onPointerUp(event) {
    if (this.draggingBoundaryPoint && event.button === 0) return this.finishBoundaryDrag(event);
    if (!this.draggingPoiId || event.button !== 0) return;
    this.finishPoiDrag(event);
  }

  onPointerCancel(event) {
    if (this.draggingBoundaryPoint) return this.finishBoundaryDrag(event);
    if (!this.draggingPoiId) return;
    this.finishPoiDrag(event);
  }

  finishPoiDrag(event) {
    const poi = this.state.pois.find((candidate) => candidate.id === this.draggingPoiId);
    if (poi) poi.manual = true;
    this.draggingPoiId = null;
    this.viewport.classList.remove("terrain-dragging-poi");
    this.viewer.releaseTerrainPointer(event.pointerId);
    this.saveState();
    this.renderInspector();
    this.renderMetrics();
    this.setStatus(`${poi?.label ?? "POI"} is now a manual override.`);
    this.detail.classList.add("mobile-open");
  }

  finishBoundaryDrag(event) {
    const point = this.draggingBoundaryPoint;
    this.draggingBoundaryPoint = null;
    this.viewport.classList.remove("terrain-dragging-boundary");
    this.viewer.releaseTerrainPointer(event.pointerId);
    this.saveState();
    this.renderInspector();
    this.setStatus(`Boundary control ${point?.id ?? "point"} is now a manual shape override.`);
    this.detail.classList.add("mobile-open");
  }
}
